import React from 'react';
import {
    Card,
    CardContent,
    CardActionArea,
    Typography,
    Box,
    Chip,
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import ArticleIcon from '@mui/icons-material/Article';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import RouteIcon from '@mui/icons-material/Route';
import GroupsIcon from '@mui/icons-material/Groups';
import { useNavigate } from 'react-router-dom';
import { SearchResult } from '../../services/searchService';
import { useTranslation } from '../../i18n';

interface SearchResultCardProps {
    result: SearchResult;
}

const SearchResultCard: React.FC<SearchResultCardProps> = ({ result }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const getTypeIcon = () => {
        switch (result.type) {
            case 'course':
                return <SchoolIcon fontSize="small" />;
            case 'lesson':
                return <ArticleIcon fontSize="small" />;
            case 'wiki':
                return <MenuBookIcon fontSize="small" />;
            case 'learning_path':
                return <RouteIcon fontSize="small" />;
            case 'learning_group':
                return <GroupsIcon fontSize="small" />;
            default:
                return <ArticleIcon fontSize="small" />;
        }
    };

    const getTypeLabel = () => {
        switch (result.type) {
            case 'course':
                return t('search.types.course');
            case 'lesson':
                return t('search.types.lesson');
            case 'wiki':
                return t('search.types.wiki');
            case 'learning_path':
                return t('search.types.learningPath');
            case 'learning_group':
                return t('search.types.learningGroup', 'Lerngruppe');
            default:
                return result.type;
        }
    };

    const getTypeColor = (): 'primary' | 'secondary' | 'success' | 'info' | 'warning' => {
        switch (result.type) {
            case 'course':
                return 'primary';
            case 'lesson':
                return 'secondary';
            case 'wiki':
                return 'success';
            case 'learning_path':
                return 'info';
            case 'learning_group':
                return 'warning';
            default:
                return 'primary';
        }
    };

    const getDifficultyLabel = () => {
        if (!result.difficulty) return null;
        switch (result.difficulty) {
            case 'beginner':
                return t('search.difficulty.beginner');
            case 'intermediate':
                return t('search.difficulty.intermediate');
            case 'advanced':
                return t('search.difficulty.advanced');
            default:
                return result.difficulty;
        }
    };

    const handleClick = () => {
        navigate(result.url);
    };

    return (
        <Card sx={{ mb: 2 }}>
            <CardActionArea onClick={handleClick}>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        {result.thumbnail && (
                            <Box
                                component="img"
                                src={result.thumbnail}
                                alt={result.title}
                                sx={{
                                    width: 80,
                                    height: 60,
                                    objectFit: 'cover',
                                    borderRadius: 1,
                                    flexShrink: 0,
                                }}
                            />
                        )}
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                                <Chip
                                    icon={getTypeIcon()}
                                    label={getTypeLabel()}
                                    size="small"
                                    color={getTypeColor()}
                                    variant="outlined"
                                />
                                {result.difficulty && (
                                    <Chip
                                        label={getDifficultyLabel()}
                                        size="small"
                                        variant="outlined"
                                    />
                                )}
                            </Box>

                            <Typography variant="h6" component="div" noWrap>
                                {result.title}
                            </Typography>

                            {result.courseTitle && (
                                <Typography variant="body2" color="primary" sx={{ mb: 0.5 }}>
                                    {t('search.fromCourse')}: {result.courseTitle}
                                </Typography>
                            )}

                            {result.excerpt && (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                    }}
                                >
                                    {result.excerpt}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </CardContent>
            </CardActionArea>
        </Card>
    );
};

export default SearchResultCard;
